
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Minus } from "lucide-react";
import { Step } from "@/types/recipe";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface StepsListProps {
  steps: Step[];
  onStepChange: (index: number, value: Partial<Step>) => void;
  onAddStep: () => void;
  onRemoveStep: (index: number) => void;
}

const StepsList = ({
  steps,
  onStepChange,
  onAddStep,
  onRemoveStep,
}: StepsListProps) => {
  const handleTimeChange = (index: number, value: string, unit: 'minutes' | 'hours') => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return;

    let timeInMinutes: string;
    if (unit === 'hours') {
      timeInMinutes = `${numValue * 60} minutes`;
    } else {
      timeInMinutes = `${numValue} minutes`;
    }

    onStepChange(index, { time: timeInMinutes });
  };

  const getTimeValue = (timeString: string | undefined): { value: string, unit: 'minutes' | 'hours' } => {
    if (!timeString) return { value: '', unit: 'minutes' };
    
    const match = timeString.match(/(\d+)\s*minutes?/);
    if (!match) return { value: '', unit: 'minutes' };
    
    const minutes = parseInt(match[1]);
    if (minutes >= 60) {
      return { value: String(Math.floor(minutes / 60)), unit: 'hours' };
    }
    return { value: String(minutes), unit: 'minutes' };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Steps</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAddStep}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Step
        </Button>
      </div>
      {steps.map((step, index) => (
        <div key={index} className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={step.instruction}
              onChange={(e) => onStepChange(index, { instruction: e.target.value })}
              placeholder={`Step ${index + 1}`}
              className="flex-1"
            />
            {steps.length > 1 && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => onRemoveStep(index)}
              >
                <Minus className="w-4 h-4" />
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={step.temperature || ''}
              onChange={(e) => onStepChange(index, { temperature: e.target.value })}
              placeholder="Temperature (e.g., 380°F)"
              className="flex-1"
            />
            <div className="flex gap-2 flex-1">
              <Input
                type="number"
                min="1"
                value={getTimeValue(step.time).value}
                onChange={(e) => {
                  const timeUnit = getTimeValue(step.time).unit;
                  handleTimeChange(index, e.target.value, timeUnit);
                }}
                placeholder="Time"
                className="flex-1"
              />
              <Select
                value={getTimeValue(step.time).unit}
                onValueChange={(value: 'minutes' | 'hours') => {
                  const timeValue = getTimeValue(step.time).value;
                  if (timeValue) {
                    handleTimeChange(index, timeValue, value);
                  }
                }}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StepsList;
